/** What every editor of the wizard designer reads: the project, the model,
    the catalog and the base transaction's forms. One context rather than a
    dozen props through every inspector — the pickers three levels down need
    the same three facts as the tab that holds them. */

import { createContext, useContext } from 'react'
import type {
  CatalogColumn, CatalogForm, CatalogTransaction, WizardCatalog,
} from '@/api/wizards'
import type { WizardModel } from './types'

export interface WizardDesignCtx {
  project: string
  model: WizardModel
  catalog: WizardCatalog | null
  /** The base transaction from the catalog (null until chosen / loaded). */
  base: CatalogTransaction | null
  readOnly: boolean
}

const Ctx = createContext<WizardDesignCtx | null>(null)

export const WizardDesignProvider = Ctx.Provider

export function useWizardDesign(): WizardDesignCtx {
  const v = useContext(Ctx)
  if (!v) throw new Error('useWizardDesign outside WizardDesignProvider')
  return v
}

/** The header form: form "1", else the first form. */
export function headerForm(base: CatalogTransaction | null): CatalogForm | null {
  if (!base || !base.forms.length) return null
  return base.forms.find((f) => f.form_no === '1') || base.forms[0]
}

/** The line forms — every form but the header. */
export function lineForms(base: CatalogTransaction | null): CatalogForm[] {
  const h = headerForm(base)
  return (base?.forms || []).filter((f) => f !== h)
}

export function formByNo(base: CatalogTransaction | null, formNo?: string | null): CatalogForm | null {
  if (!base) return null
  const no = String(formNo || '').trim()
  return base.forms.find((f) => f.form_no === no) || null
}

/** The form `{line.X}` reads in a step: the step's own `form_no`, else the
 *  wizard's `line_form`. */
export function lineFormFor(
  base: CatalogTransaction | null, model: WizardModel, stepFormNo?: string,
): CatalogForm | null {
  return formByNo(base, stepFormNo || model.line_form || '2')
    || lineForms(base)[0] || null
}

export function findColumn(form: CatalogForm | null, name: string): CatalogColumn | null {
  if (!form || !name) return null
  const n = name.toLowerCase()
  return form.columns.find((c) => c.name.toLowerCase() === n || (c.db_name || '').toLowerCase() === n) || null
}

/** The column a `header.X` / `line.X` target names (null for vars / unknown). */
export function columnOfTarget(
  ctx: Pick<WizardDesignCtx, 'base' | 'model'>, target: string, stepFormNo?: string,
): CatalogColumn | null {
  const m = /^(header|line|form(\d+)|line(\d+))\.(.+)$/i.exec(String(target || '').trim())
  if (!m) return null
  const scope = m[1].toLowerCase()
  if (scope === 'header') return findColumn(headerForm(ctx.base), m[4])
  if (scope === 'line') return findColumn(lineFormFor(ctx.base, ctx.model, stepFormNo), m[4])
  const no = m[2] || m[3]
  return findColumn(formByNo(ctx.base, no), m[4])
}

/** The token groups the expression / template inputs offer as insert chips. */
export interface TokenGroup {
  label: string
  tokens: string[]
}

export function tokenGroups(
  ctx: Pick<WizardDesignCtx, 'base' | 'model'>, stepFormNo?: string,
  extra: TokenGroup[] = [],
): TokenGroup[] {
  const header = headerForm(ctx.base)
  const line = lineFormFor(ctx.base, ctx.model, stepFormNo)
  const groups: TokenGroup[] = [
    ...extra,
    { label: 'Header', tokens: (header?.columns || []).map((c) => `{header.${c.name}}`) },
    { label: 'Current line', tokens: (line?.columns || []).map((c) => `{line.${c.name}}`) },
    { label: 'All lines', tokens: (line?.columns || []).map((c) => `{lines.${c.name}}`) },
    { label: 'Variables', tokens: (ctx.model.vars || []).map((v) => `{var.${v.name}}`) },
    { label: 'Inputs', tokens: (ctx.model.inputs || []).map((i) => `{input.${i.name}}`) },
    { label: 'Steps', tokens: ctx.model.steps.map((s) => `{step.${s.id}.value}`) },
    { label: 'Run', tokens: ['{run.txn_id}', '{run.mode}', '{run.save_status}', '{user.id}', '{user.name}', '{today}', '{now}'] },
  ]
  return groups.filter((g) => g.tokens.length > 0)
}
